import { List, Map as ImmutableMap, Record as ImmutableRecord } from 'immutable';
import type { RecordOf } from 'immutable';
import ExtendableError from 'es6-error';

import { accountsTypes } from 'companion-app-components/flux/accounts';
import type { Institution } from 'companion-app-components/flux/institutions/institutionsTypes';

import type { InstitutionLogin, MfaChallenge } from 'data/institutionLogins/types';

export const DIALOG_TYPE_ACCOUNTS_DISCOVERY = 'DIALOG_TYPE_ACCOUNTS_DISCOVERY';
export type AccountDiscoveryMode =
  | 'ADD-FI' 
  | 'PROVIDE-CREDENTIALS' 
  | 'PROVIDE-MFA' 
  | 'UPDATE-CREDENTIALS' 
  | 'ACCOUNT-REDISCOVERY' 
  | 'RE-AUTHENTICATE' 
  | 'UPDATE-FI' 
  | 'MIGRATE-INSTITUTION-LOGIN';

type AccountDiscoveryDialogPropsProps = {
  mode: AccountDiscoveryMode,
  initiator?: string,
  institutionLogin: InstitutionLogin,
  mfaChallenge?: MfaChallenge,
  aggAction?: any,
  afterCloseAction?: any,
  onClose?: any,
};

export type AccountDiscoveryDialogProps = RecordOf<AccountDiscoveryDialogPropsProps>;
export const mkAccountDiscoveryDialogProps = ImmutableRecord<AccountDiscoveryDialogPropsProps>({
  mode: 'ADD-FI',
  initiator: undefined,
  institutionLogin: undefined,
  mfaChallenge: undefined,
  aggAction: undefined,
  afterCloseAction: undefined,
  onClose: undefined,
});

// =============================================================================
// Account Discovery Data
// =============================================================================

type DiscoveredAccountProps = {
  shadowAccountId: string | null,
  id: string | null,
  cpId: string | null,
  cpLoginId: string | null,
  channel: string | null,
  type: string | null,
  subType?: string | null,
  name: string | null,
  number: string | null,
  maskedNumber: string | null,
  balance: number | null,
  balanceAt: string | null,
  currency: string | null,
};

export type DiscoveredAccount = RecordOf<DiscoveredAccountProps>;
export const mkDiscoveredAccount = ImmutableRecord<DiscoveredAccountProps>({
  shadowAccountId: null,
  id: null,
  cpId: null,
  cpLoginId: null,
  channel: null,
  type: null,
  subType: null,
  name: null,
  number: null,
  maskedNumber: null,
  balance: null,
  balanceAt: null,
  currency: null,
});

type ActiveDiscoveredAccountProps = {
  account: any,
  discoveredAccount: DiscoveredAccount
};

export type ActiveDiscoveredAccount = RecordOf<ActiveDiscoveredAccountProps>;
export const mkActiveDiscoveredAccount = ImmutableRecord<ActiveDiscoveredAccountProps>({
  account: accountsTypes.mkAccount('UNKNOWN'),
  discoveredAccount: mkDiscoveredAccount(),
});

export type AccountDiscoveryDataProps = {
  institutionLoginId: string | null,
  credentialsBlob?: string | null,
  mode?: string | null,
  status: string | null,
  activeAccounts?: List<ActiveDiscoveredAccount> | null,
  deadAccounts?: List<accountsTypes.Account> | null,
  newAccounts?: List<DiscoveredAccount> | null,
  unconnectedAccounts?: List<accountsTypes.Account> | null,
};

export type AccountDiscoveryData = RecordOf<AccountDiscoveryDataProps>;
export const mkAccountDiscoveryData = ImmutableRecord<AccountDiscoveryDataProps>({
  institutionLoginId: null,
  credentialsBlob: null,
  status: null,
  activeAccounts: null,
  deadAccounts: null,
  newAccounts: null,
  unconnectedAccounts: null,
});

// =============================================================================
// InstitutionLoginUpserter
// =============================================================================

type InstitutionLoginUpserterDataProps = {
  institution?: Institution,
  channel?: string,
  institutionLogin?: InstitutionLogin,
  institutionLoginIdBeingMigrated?: string,
  // contains MFA challenge data if server has requested an MFA challenge
  mfaChallenge?: MfaChallenge,
  // contains partnerAuthUris after oauth flow (if authType is PARTNER_AUTH
  partnerAuthUris?: List<string>,
  // currently used for Plaid when intializing link (may be used by other aggregators in future)
  authToken?: string,
  // contains discovered accounts data (if discovering accounts and discovered data is available
  accountDiscoveryData?: AccountDiscoveryData,
  updateStrategy: 'REPLACE' | 'UPDATE',
  isAuthenticationDone: boolean,
  isAccountUpsertingDone: boolean,
  isAccountRefreshingDone: boolean,
  isPolling: false,
  isSubmitting: boolean,
  error?: Record<string, any>,
  publicToken?: string,
};

export type InstitutionLoginUpserterData = RecordOf<InstitutionLoginUpserterDataProps>;
export const mkInstitutionLoginUpserterData = ImmutableRecord<InstitutionLoginUpserterDataProps>({
  institution: undefined,
  channel: undefined,
  institutionLogin: undefined,
  institutionLoginIdBeingMigrated: undefined,
  mfaChallenge: undefined,
  partnerAuthUris: undefined,
  publicToken: undefined,
  accountDiscoveryData: undefined,
  updateStrategy: 'UPDATE',
  isAuthenticationDone: false,
  isAccountUpsertingDone: false,
  isAccountRefreshingDone: false,
  isPolling: false,
  isSubmitting: false,
  error: undefined,
});

export type InstitutionLoginUpserterStoreProps = {
  upserterData: ImmutableMap<string, InstitutionLoginUpserterData>,
};

export type InstitutionLoginUpserterStore = RecordOf<InstitutionLoginUpserterStoreProps>;
export const mkInstitutionLoginUpserterStore = ImmutableRecord<InstitutionLoginUpserterStoreProps>({
  upserterData: ImmutableMap(),
});

// ================================================================================================
// Types AccountDiscovery Status
// ================================================================================================

type AccountDiscoveryAggregatorStatusProps = {
  isProcessing: boolean,
  aggStatus: string,
  cpAggStatusCode?: string,
  cpAggStatusDetail?: string,
  userInstructions?: string,
};
export type AccountDiscoveryAggregatorStatus = RecordOf<AccountDiscoveryAggregatorStatusProps>;
export const mkAccountDiscoveryAggregatorStatus = ImmutableRecord<AccountDiscoveryAggregatorStatusProps>({
  isProcessing: false,
  aggStatus: 'unknown',
  cpAggStatusCode: undefined,
  cpAggStatusDetail: undefined,
  userInstructions: undefined,
});

type AccountDiscoveryStatusProps = {
  institutionLoginId: string | null,
  isProcessing: boolean,
  status: string,
  aggregators: List<AccountDiscoveryAggregatorStatus>,
};

export type AccountDiscoveryStatus = RecordOf<AccountDiscoveryStatusProps>;
export const mkAccountDiscoveryStatus = ImmutableRecord<AccountDiscoveryStatusProps>({
  institutionLoginId: null,
  isProcessing: false,
  status: '',
  aggregators: List(),
});

// ================================================================================================
// Account Upserter Types
// ================================================================================================

export type AccountActionTypes = 'ADD_ACCOUNT' | 'IGNORE' | 'LINK_TO';

export type AccountActionProps = {
  id: string,
  type: AccountActionTypes,
  menuItemLabel: string,
  selectedItemLabel: string,
  accountToLinkTo?: accountsTypes.Account | null,
};

export type AccountAction = RecordOf<AccountActionProps>;
export const mkAccountAction = ImmutableRecord<AccountActionProps>({
  id: '0',
  type: 'LINK_TO',
  menuItemLabel: 'Link to',
  selectedItemLabel: 'Link',
  accountToLinkTo: null,
});

export const ACCOUNT_ACTION_ADD = mkAccountAction({
  id: 'ADD',
  type: 'ADD_ACCOUNT',
  menuItemLabel: 'Add',
  selectedItemLabel: 'Add',
});

export const ACCOUNT_ACTION_IGNORE = mkAccountAction({
  id: 'IGNORE',
  type: 'IGNORE',
  menuItemLabel: 'Ignore',
  selectedItemLabel: 'Ignore',
});

export type AccountData = {
  action: AccountAction,
  name: string,
  type: string,
  discoveredAccount: DiscoveredAccount,
  linkableAccountsSection1?: List<accountsTypes.Account>,
  linkableAccountsSection2?: List<accountsTypes.Account>,
  linkableAccountsSection3?: List<accountsTypes.Account>,
  formFieldName: string,
};

// ================================================================================================
// Account Discovery Error Types
// ================================================================================================

export class AccountDiscoveryFailure extends ExtendableError {
  public aggStatus: string;

  public aggStatusCode: string;

  constructor(message: string, aggStatus: string, aggStatusCode: string) {
    super(message);
    this.aggStatus = aggStatus;
    this.aggStatusCode = aggStatusCode;
  }
}

// Property 'aggStatus' does not exist on type 'AccountDiscoveryFailure'.

// ================================================================================================
// Aggregation Info Types
// ================================================================================================

type AggregationInfoProps = {
  type: 'FINICITY' | 'PLAID',
};
export type AggregationInfo = RecordOf<AggregationInfoProps>;

type FinicityAggregationInfoProps = AggregationInfoProps & {
  connectUrl?: string,
  pollingRef?: string,
};

export type FinicityAggregationInfo = RecordOf<FinicityAggregationInfoProps>;

export const mkFinicityAggregationInfo = ImmutableRecord<FinicityAggregationInfoProps>({
  type: 'FINICITY',
  connectUrl: undefined,
  pollingRef: undefined,
});

type PlaidAggregationInfoProps = AggregationInfoProps & {
  linkToken: string | null,
};

export type PlaidAggregationInfo = RecordOf<PlaidAggregationInfoProps>;

export const mkPlaidAggregationInfo = ImmutableRecord<PlaidAggregationInfoProps>({
  type: 'PLAID',
  linkToken: null,
});

export const REDUCER_KEY = 'upsertInstitutionLoginStore';
export type ReducerKey = typeof REDUCER_KEY;

declare module 'companion-app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [REDUCER_KEY]: InstitutionLoginUpserterStore;
  }
}
